import { useMemo } from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'

import {
  Bar,
  BarChart,
  CartesianGrid,
  Label,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { ChartFrame } from '../ChartFrame'
import { useChartSeries } from '../ChartSeriesContext'
import { ChartTooltipView } from '../ChartTooltipView'
import { ComparisonHatchBar } from '../comparisonHatch'
import {
  axisTickLabels,
  buildSeriesRows,
  CHART_MARGIN,
  computeYAxisWidth,
  computeYDomain,
  DEFAULT_X_AXIS_HEIGHT,
  fitToGrid,
  makeFormatDateTick,
  TICK_FONT_SIZE,
  useChartChrome,
  X_AXIS_MIN_TICK_GAP,
  X_AXIS_PADDING,
  Y_AXIS_LABEL_OFFSET,
  Y_AXIS_TICK_MARGIN,
  Y_AXIS_WIDTH,
} from '../cartesianChart'
import type { BucketGranularity } from '../bucketSeries'
import { formatBucketDate } from '../chartFormatting'
import { makeYAxisTickFormatter } from '../formatYAxisTick'
import { yTicksForDomain } from '../yAxisTicks'
import {
  pickComparisonXTicks,
  StackedDateTick,
  X_AXIS_HEIGHT_STACKED,
} from '../stackedDateTick'
import { MetricUnit } from '../types'
import { chartXAxisTicks } from '../xAxisTicks'

/** Rounded top corners on each column (flat for a stacked column's segments). */
const BAR_RADIUS: [number, number, number, number] = [2, 2, 0, 0]

export interface ColumnChartProps {
  /** Accessible name for the chart (e.g. "Posts per day by channel"). */
  ariaLabel: string
  height?: number
  /** Stack the series into a single column per period; otherwise render them
   *  grouped side by side (the default). */
  stacked?: boolean
  startDate?: Date
  endDate?: Date
  /** When a comparison sibling is present, show only the comparison-period dates
   *  on the x-axis (the "Comparison" period toggle). */
  comparisonOnly?: boolean
  /** Overlay shown when there's no data; the axes/grid stay visible. */
  emptyState?: ReactNode
  /** Show each series' icon in the tooltip (e.g. channel icons). */
  showIconsInTooltip?: boolean
  /** Disable enter animations (used by the offscreen export snapshot). */
  disableAnimations?: boolean
  /** Month-only date labels for monthly-bucketed data. */
  monthly?: boolean
  /** Bucket size the columns were aggregated into. Bucketed (`week`/`month`) views
   *  label every bucket so the ticks stay evenly spaced; `day` (the default) thins
   *  to a legible set. */
  granularity?: BucketGranularity
  /** Y-axis label; defaults to the first series' label. */
  yAxisLabel?: string
}

/**
 * The base column chart (Image #1/#2): per-period counts (e.g. Posts) drawn as
 * vertical columns from {@link ChartSeriesContext}, grouped side by side or
 * stacked into one column per period when `stacked`. Presentational and
 * style-owning — colour is never a prop. A period with no count is simply no
 * column (not "no data"), so there is no greyed-line / no-data treatment here.
 * A comparison sibling renders as a lighter grouped column, with its
 * comparison-period date stacked beneath the primary date on the x-axis.
 * Keyboard-navigable via Recharts `accessibilityLayer`, with a screen-reader
 * summary + data table.
 */
export function ColumnChart({
  ariaLabel,
  height = 250,
  stacked = false,
  startDate,
  endDate,
  comparisonOnly = false,
  emptyState,
  showIconsInTooltip = false,
  disableAnimations = false,
  monthly = false,
  granularity = 'day',
  yAxisLabel,
}: ColumnChartProps): JSX.Element {
  const series = useChartSeries()
  const { resolvedColors, gridColor, neutralTextColor, subtleTextColor } =
    useChartChrome(series)

  const { data, comparisonDateByTimestamp } = useMemo(
    () => buildSeriesRows(series),
    [series],
  )

  const empty = emptyState != null
  const yDomain = useMemo(
    () => computeYDomain(data, series, { column: true, stacked, empty }),
    [data, series, stacked, empty],
  )

  const formatDateTick = useMemo(
    () => makeFormatDateTick(startDate, endDate, monthly),
    [startDate, endDate, monthly],
  )
  // The tooltip header reads the full period a column covers (a day, a week range,
  // or a month), so a bucketed column doesn't read as a single day.
  const formatTooltipDate = useMemo(
    () =>
      (timestampMs: number): string =>
        formatBucketDate(timestampMs, granularity),
    [granularity],
  )

  // With a comparison sibling, pin ticks to the aligned comparison timestamps
  // and stack each comparison-period date beneath its primary date. Otherwise hand
  // recharts an evenly-thinned default set — its own thinning squishes the labels
  // toward the end of the range.
  const hasComparison = comparisonDateByTimestamp.size > 0
  const comparisonXTicks = useMemo(
    () => pickComparisonXTicks(data, comparisonDateByTimestamp),
    [data, comparisonDateByTimestamp],
  )
  const xTicks = useMemo(() => data.map((d) => d.timestamp), [data])
  const xAxisHeight =
    hasComparison && !comparisonOnly ? X_AXIS_HEIGHT_STACKED : undefined
  const effectiveXAxisHeight = xAxisHeight ?? DEFAULT_X_AXIS_HEIGHT
  const { chartHeight, tickCount } = fitToGrid(height, effectiveXAxisHeight)
  const yAxis = useMemo(
    () =>
      yTicksForDomain(yDomain, tickCount, {
        integer: series[0]?.unit !== MetricUnit.PERCENTAGE,
        zeroBaseline: true,
      }),
    [yDomain, tickCount, series],
  )

  const axisLabel = yAxisLabel ?? series[0]?.label

  // Size the band to its own tick labels so the numbers stay 12px from the
  // rotated axis label whatever their width (see computeYAxisWidth).
  const axisWidth = useMemo(
    () =>
      empty
        ? Y_AXIS_WIDTH
        : computeYAxisWidth(
            axisTickLabels(
              yAxis.ticks,
              makeYAxisTickFormatter(yAxis.domain, series[0]?.unit, tickCount),
            ),
          ),
    [empty, yAxis, series, tickCount],
  )

  return (
    <ChartFrame
      ariaLabel={ariaLabel}
      monthly={monthly}
      emptyState={emptyState}
      height={chartHeight}
    >
      <BarChart
        data={data}
        margin={CHART_MARGIN}
        accessibilityLayer
        aria-label={ariaLabel}
      >
        {/* Bind to the left axis: recharts' default yAxisId 0 is a hidden implicit
            axis, so without this the gridlines wouldn't sit on our ticks. */}
        <CartesianGrid yAxisId="left" vertical={false} stroke={gridColor} />

        <XAxis
          dataKey="timestamp"
          type="category"
          ticks={chartXAxisTicks(granularity, xTicks, {
            comparisonTicks: hasComparison ? comparisonXTicks : undefined,
          })}
          // Daily: recharts keeps both ends and drops only interior labels that
          // would collide on a narrow (mobile) axis (desktop fits them all).
          // Bucketed (week/month) and comparison views keep every tick — thinning
          // would strip labels from under bars, and comparison's formatter-less
          // ticks would mis-measure the raw timestamp.
          interval={
            hasComparison || granularity !== 'day' ? 0 : 'preserveStartEnd'
          }
          minTickGap={X_AXIS_MIN_TICK_GAP}
          tickFormatter={hasComparison ? undefined : formatDateTick}
          tick={
            hasComparison
              ? (props) => (
                  <StackedDateTick
                    {...props}
                    color={neutralTextColor}
                    comparisonDateByTimestamp={comparisonDateByTimestamp}
                    comparisonOnly={comparisonOnly}
                    formatTick={formatDateTick}
                  />
                )
              : { fontSize: TICK_FONT_SIZE, fill: neutralTextColor, dy: 8 }
          }
          height={effectiveXAxisHeight}
          axisLine={{ stroke: gridColor }}
          tickLine={false}
          padding={{ left: X_AXIS_PADDING, right: X_AXIS_PADDING }}
        />

        <YAxis
          yAxisId="left"
          orientation="left"
          domain={yAxis.domain}
          ticks={yAxis.ticks}
          width={axisWidth}
          tick={{ fontSize: TICK_FONT_SIZE, fill: subtleTextColor }}
          axisLine={false}
          tickLine={false}
          tickSize={0}
          tickMargin={Y_AXIS_TICK_MARGIN}
          tickFormatter={
            empty
              ? undefined
              : makeYAxisTickFormatter(yAxis.domain, series[0]?.unit, tickCount)
          }
        >
          {axisLabel && (
            <Label
              value={axisLabel}
              angle={-90}
              position="insideLeft"
              offset={Y_AXIS_LABEL_OFFSET}
              style={{
                fill: subtleTextColor,
                fontSize: TICK_FONT_SIZE,
                textAnchor: 'middle',
              }}
            />
          )}
        </YAxis>

        <Tooltip
          content={(props) => (
            <ChartTooltipView
              {...props}
              showIcons={showIconsInTooltip}
              formatDate={formatTooltipDate}
              // Columns mirror their hatched comparison bar.
              comparisonSwatch={() => 'hatched'}
            />
          )}
          cursor={{ fill: 'transparent' }}
        />

        {series.map((s) => {
          const isComparison = s.kind === 'comparison'
          // Round column tops; a stack rounds only via its top segment, so
          // leave stacked segments flat to avoid rounding each one.
          const radius = stacked ? undefined : BAR_RADIUS
          return (
            <Bar
              key={s.id}
              dataKey={s.id}
              yAxisId="left"
              stackId={stacked ? 'stack' : undefined}
              fill={resolvedColors[s.id]}
              radius={radius}
              shape={
                isComparison ? (
                  <ComparisonHatchBar radius={radius} />
                ) : undefined
              }
              isAnimationActive={!disableAnimations}
            />
          )
        })}
      </BarChart>
    </ChartFrame>
  )
}
